<template>
  <b-container fluid>
    <b-col md="6">
      <b-card :title="title">
        <p v-if="status === 'expired'">
          Sorry, this invitation has been used. If you need a new invitation, please contact your supervisor.
        </p>

        <form v-if="status === 'pending'" v-on:submit.prevent.stop="submit">
          <div v-if="invitation">
            <p>{{invitation.invitation_message}}</p>
            <p v-if="isCompanyAdmin">You will be a company admin of {{invitation.copmany}}.</p>
            <p v-if="adminTeams.length > 0">You will be a team admin of:</p>
            <b-list-group v-if="adminTeams.length > 0" class="mb-3">
              <b-list-group-item v-for="(team, teamIndex) in adminTeams" :key="teamIndex">
                {{team.name}}
              </b-list-group-item>
            </b-list-group>

            <p v-if="memberTeams.length > 0">You will be a team member of:</p>
            <b-list-group v-if="memberTeams.length > 0" class="mb-3">
              <b-list-group-item v-for="(team, teamIndex) in memberTeams" :key="teamIndex">
                {{team.name}}
              </b-list-group-item>
            </b-list-group>
            <h4>Account Details</h4>
          </div>

          <b-form-group id="name_label" label="Your Name" label-for="name">
            <b-input id="name" type="text" v-model="user.name" required></b-input>
          </b-form-group>
          <b-form-group id="email_label" label="Your Email Address" label-for="email">
            <b-input id="email" type="email" v-model="user.email" required></b-input>
          </b-form-group>
          <b-form-group id="new_password_label" label="New Password" label-for="new_password">
            <b-input id="new_password" type="password" v-model="user.password" required></b-input>
          </b-form-group>
          <b-alert variant="warning" :show="showPasswordWarning">Password is not secure. {{passwordWarning}}</b-alert>
          <b-form-group id="confirm_new_password_label" label="Confirm New Password" label-for="confirm_new_password">
            <b-input id="confirm_new_password" type="password" v-model="user.confirmPassword" required></b-input>
          </b-form-group>
          <div v-if="invitation === null">
            <hr>
            <b-form-group id="company_name_label" label="Company Name" label-for="company_name">
              <b-input id="company_name" type="text" v-model="company.name" required></b-input>
            </b-form-group>
            <b-form-group id="company_tel_label" label="Telephone" label-for="company_tel">
              <b-input id="company_tel" type="text" v-model="company.tel" required></b-input>
            </b-form-group>
            <b-form-group label="Company Address">
              <b-form-textarea :rows="5" v-model="company.address" required>
              </b-form-textarea>
              <b-form-text tag="p">
                If you are new to Vos Voco, we will get in touch to determine your needs and help you pick a workflow suitable for your business.
              </b-form-text>
            </b-form-group>
          </div>

          <b-form-group v-if="isManaged">
            <b-form-checkbox v-model="user.acceptManage">
              Allow {{invitation.company.name}} to manage my account, including team memberships and notifications
            </b-form-checkbox>
          </b-form-group>
          <p>
            Your account will be subject to the
            <a href="https://www.vosvoco.com/privacy" target="_blank">Vos Voco Privacy Policy</a>.
          </p>
          <b-alert variant="danger" :show="errorMessage != null">{{errorMessage}}</b-alert>
          <p class="text-right mb-0">
            <b-button to="/" variant="secondary" class="mb-1">Cancel</b-button>
            <b-button type="submit" variant="primary" class="mb-1">Create Account</b-button>
          </p>
        </form>

      </b-card>
    </b-col>
  </b-container>
</template>

<script>
import API from '@/lib/api'
import Icon from '@/components/Icon'
import zxcvbn from 'zxcvbn'

export default {
  name: 'NewAccount',
  components: { Icon },
  data () {
    return {
      invitationToken: this.$route.params.token,
      invitation: null,
      status: 'loading',
      action: 'check',
      showPasswordWarning: false,
      passwordWarning: null,
      user: {
        acceptManage: false,
        name: '',
        email: '',
        password: ''
      },
      company: {
        name: '',
        tel: '',
        address: ''
      },
      errorMessage: null
    }
  },
  computed: {
    title () {
      if (this.status === 'loading') {
        return 'Loading...'
      } else {
        return 'Create Your Account'
      }
    },
    isManaged () {
      return this.invitation != null && this.invitation.user.company_users.find((cu) => cu.is_managed) != null
    },
    isCompanyAdmin () {
      return this.invitation != null && this.invitation.user.company_users.find((cu) => cu.is_admin) != null
    },
    adminTeams () {
      if (this.invitation == null) {
        return []
      } else {
        return this.invitation.user.team_users.filter((tu) => tu.is_admin).map((tu) => tu.team)
      }
    },
    memberTeams () {
      if (this.invitation == null) {
        return []
      } else {
        return this.invitation.user.team_users.filter((tu) => tu.is_member).map((tu) => tu.team)
      }
    }
  },
  watch: {
    'user.password': function () {
      const result = zxcvbn(this.user.password)
      this.showPasswordWarning = (result.score < 3)
      if (result.feedback != null && result.feedback.warning != null && result.feedback.warning !== '') {
        this.passwordWarning = result.feedback.warning
      } else {
        this.passwordWarning = result.feedback.suggestions.join('. ')
      }
    }
  },
  mounted () {
    this.fetchData()
  },
  methods: {
    fetchData () {
      if (this.invitationToken != null && this.invitationToken !== '') {
        API.get(`invitations/${this.invitationToken}`)
          .then((response) => {
            this.invitation = response.data.invitation
            this.status = this.invitation.status
            this.user.name = this.invitation.user.name
            this.user.email = this.invitation.user.email
          })
          .catch((error) => {
            if (error.response && error.response.status === 401) {
              this.invitation = null
              this.status = 'error'
            }
          })
      } else {
        this.status = 'pending'
      }
    },
    submit () {
      if (this.user.password !== this.user.confirmPassword) {
        this.errorMessage = 'Password does not match confirmation'
      } else {
        if (this.invitation != null) {
          this.submitInvitation()
        } else {
          this.submitNewAccount()
        }
      }
    },
    submitNewAccount () {
      const params = {
        user: {
          name: this.user.name,
          email: this.user.email,
          password: this.user.password
        },
        company: {
          name: this.company.name,
          tel: this.company.tel,
          address: this.company.address
        }
      }
      API.post('/account', params, {})
        .then((response) => {
          this.$root.showToast('Account Created')
          this.$router.push('/account/created')
        }).catch((error) => {
          if (error.response && error.response.status === 422) {
            this.errorMessage = error.response.data.message
          }
        })
    },
    submitInvitation () {
      this.errorMessage = null
      if (this.isManaged && !this.user.acceptManage) {
        this.errorMessage = `You must allow ${this.invitation.company.name} to manage your account in order to accept this invitation.`
      } else {
        const params = {
          user: {
            name: this.user.name,
            email: this.user.email,
            password: this.user.password
          },
          token: this.invitationToken
        }
        API.post('/account', params, {})
          .then((response) => {
            this.$root.showToast('Invitation Accepted')
            this.$router.push('/account/created')
          })
          .catch((error) => {
            if (error.response && error.response.status === 422) {
              this.errorMessage = error.response.data.message
            }
          })
      }
    },
    decline () {

    }
  }
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
</style>



// WEBPACK FOOTER //
// src/pages/NewAccount.vue